import { useState, useRef, useCallback, useEffect } from "react";

const MENU_ITEM_QUERY =
   "button, [data-testid='dropdown-option-button'], .dropdown-button-item, [role='menuitem'], [role='option'], a[href], [data-value], [tabindex]:not([tabindex='-1'])";

const simulateClick = (el) => {
   if (!el) return;
   const opts = { bubbles: true, cancelable: true, view: window };
   el.dispatchEvent(new MouseEvent("mousedown", opts));
   el.dispatchEvent(new MouseEvent("mouseup", opts));
   el.dispatchEvent(new MouseEvent("click", opts));
};

const getDropdownContainerById = (popupId) => {
   if (!popupId) return null;
   return document.getElementById(popupId) || null;
};

const getDropdownRoot = () => document.getElementById("dropdown-root") || null;

const getAnyDropdownContainer = () => {
   const root = getDropdownRoot();
   if (!root) return null;
   return (
      root.querySelector(".items-container") ||
      root.querySelector('[role="menu"]') ||
      root.querySelector("[data-bp2-dropdown]") ||
      null
   );
};

const getAllDropdownPortals = () =>
   [document.getElementById("dropdown-root"), document.getElementById("calendar-root")].filter(
      Boolean,
   );

const getDropdownItems = (container) =>
   Array.from(container.querySelectorAll(MENU_ITEM_QUERY)).filter(
      (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
   );

const focusFirstDropdownItem = ({ popupId = null, fallback = null } = {}) => {
   let tries = 0;
   const tick = () => {
      const container = getDropdownContainerById(popupId) || getAnyDropdownContainer();
      if (container) {
         const items = getDropdownItems(container);
         if (items.length) {
            items[0].focus({ preventScroll: true });
            return;
         }
      }
      if (++tries < 16) requestAnimationFrame(tick);
      else if (fallback?.focus) fallback.focus({ preventScroll: true });
   };
   requestAnimationFrame(tick);
};

const findActionsTriggerInCell = (cellEl, actionColIndex) => {
   if (!cellEl) return null;
   const rowEl = cellEl.closest("tr, [role='row']");
   if (!rowEl) return null;
   const actionsCell =
      rowEl.querySelector(`[data-grid-cell="true"][data-col="${actionColIndex}"]`) || rowEl;
   return (
      actionsCell.querySelector(
         "[data-actions-trigger], .table-actions-trigger, button, [role='button']",
      ) || null
   );
};

const CLOSED_MENU = { row: null, trigger: null, popupId: null, open: false, openCell: null };

export const useTableDropdownMenu = ({ tableRootRef, actionColIndex, navRef }) => {
   const menuRef = useRef({ ...CLOSED_MENU });
   const [isMenuOpen, setIsMenuOpen] = useState(false);

   const isInAllowedPortal = useCallback((target) => {
      if (!target) return false;
      return getAllDropdownPortals().some((portal) => portal === target || portal.contains(target));
   }, []);

   const findActionsCellInRow = useCallback(
      (row) => {
         if (actionColIndex < 0) return null;
         return (
            tableRootRef.current?.querySelector(
               `[data-grid-cell="true"][data-row="${row}"][data-col="${actionColIndex}"]`,
            ) || null
         );
      },
      [actionColIndex],
   );

   const closeMenu = useCallback(({ returnFocusTo = null, method = "program" } = {}) => {
      const { open, trigger } = menuRef.current;
      if (!(open && trigger && document.contains(trigger))) return;
      simulateClick(trigger);
      menuRef.current = { ...CLOSED_MENU };
      if (returnFocusTo?.focus && method !== "tab" && method !== "tab+shift") {
         requestAnimationFrame(() => returnFocusTo.focus({ preventScroll: true }));
      }
      setIsMenuOpen(false);
   }, []);

   // Close on ESC and outside click
   useEffect(() => {
      const isOursOpen = () =>
         menuRef.current.open &&
         menuRef.current.trigger &&
         document.contains(menuRef.current.trigger);

      const getBackTo = () =>
         menuRef.current.openCell ||
         tableRootRef.current?.querySelector('[data-grid-cell="true"][tabindex="0"]') ||
         null;

      const onEsc = (e) => {
         if (e.key !== "Escape" || !isOursOpen()) return;
         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation?.();
         closeMenu({ returnFocusTo: getBackTo(), method: "escape" });
      };

      // Fallback for environments where keydown is swallowed
      const onEscUp = (e) => {
         if (e.key !== "Escape" || !isOursOpen()) return;
         closeMenu({ returnFocusTo: getBackTo(), method: "escape" });
      };

      const onMouseDown = (e) => {
         if (!isOursOpen()) return;
         const { trigger } = menuRef.current;
         const popup = getAnyDropdownContainer();
         const target = e.target;
         if (trigger && (target === trigger || trigger.contains(target))) return;
         if (popup && popup.contains(target)) return;
         closeMenu({ returnFocusTo: getBackTo(), method: "outside" });
      };

      window.addEventListener("keydown", onEsc, true);
      window.addEventListener("keyup", onEscUp, true);
      window.addEventListener("mousedown", onMouseDown, true);
      return () => {
         window.removeEventListener("keydown", onEsc, true);
         window.removeEventListener("keyup", onEscUp, true);
         window.removeEventListener("mousedown", onMouseDown, true);
      };
   }, [closeMenu]);

   // Keep arrow navigation inside open dropdown
   useEffect(() => {
      const onKeysCapture = (e) => {
         if (!menuRef.current.open) return;
         const container =
            getDropdownContainerById(menuRef.current.popupId) || getAnyDropdownContainer();
         if (!container) return;
         const isFromPopup =
            container.contains(e.target) ||
            container.contains(document.activeElement || document.body);
         if (!isFromPopup) return;
         if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) return;

         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation?.();

         const items = getDropdownItems(container);
         if (!items.length) return;
         const idx = items.indexOf(document.activeElement);
         if (idx === -1) {
            items[0].focus({ preventScroll: true });
            return;
         }
         if (e.key === "ArrowDown") items[(idx + 1) % items.length].focus({ preventScroll: true });
         if (e.key === "ArrowUp")
            items[(idx - 1 + items.length) % items.length].focus({ preventScroll: true });
      };

      document.addEventListener("keydown", onKeysCapture, true);
      return () => document.removeEventListener("keydown", onKeysCapture, true);
   }, []);

   const onToggleActions = useCallback(
      ({ row, col, cellEl }) => {
         const actionsCell = findActionsCellInRow(row);
         const targetCell = actionsCell || cellEl;
         const targetCol = actionsCell ? actionColIndex : col;

         navRef.current?.setActive?.({ row, col: targetCol });
         navRef.current?.focusCell?.(row, targetCol);

         const trigger = findActionsTriggerInCell(targetCell, actionColIndex);
         if (!trigger) return;

         menuRef.current.openCell = targetCell;
         simulateClick(trigger);

         requestAnimationFrame(() => {
            const controls = trigger.getAttribute("aria-controls");
            const aria = trigger.getAttribute("aria-expanded");
            const isOpen =
               aria === "true" ||
               aria === null ||
               !!(controls && getDropdownContainerById(controls)) ||
               !!getAnyDropdownContainer();
            menuRef.current = isOpen
               ? { row, trigger, popupId: controls || null, open: true, openCell: targetCell }
               : { ...CLOSED_MENU };
            setIsMenuOpen(isOpen);
            if (isOpen) {
               requestAnimationFrame(() =>
                  focusFirstDropdownItem({ popupId: controls || null, fallback: targetCell }),
               );
            }
         });
      },
      [findActionsCellInRow, actionColIndex],
   );

   return { isMenuOpen, closeMenu, isInAllowedPortal, onToggleActions };
};
